/**
 * The figure's geometry, ported verbatim from the Claude Design source
 * (`MuscleMap.dc.html`).
 *
 * Everything is a `<path d="...">` — the design synthesises ellipses and
 * rounded rects as path strings rather than using `<ellipse>`/`<rect>`. That is
 * what lets one geometry table serve both platforms: `react-native-svg`'s
 * `<Path>` takes the same `d` string the browser does, so there is no shape
 * here the mobile renderer cannot draw.
 *
 * Coordinates are in viewBox units and are meaningless outside
 * {@link MUSCLE_MAP_VIEWBOX}.
 */

import type { MuscleGroupShapes, MuscleShape } from './muscle-map.types';

/** The coordinate space every path below is authored in. */
export const MUSCLE_MAP_VIEWBOX = { width: 240, height: 560 } as const;

/**
 * An ellipse as two arc segments. Both arcs sweep the same direction with
 * `large-arc` set, which is what closes the shape — flipping either flag
 * produces a lens rather than an ellipse.
 */
export function ellipsePath(
  cx: number,
  cy: number,
  rx: number,
  ry: number
): string {
  return `M ${cx - rx} ${cy} a ${rx} ${ry} 0 1 0 ${2 * rx} 0 a ${rx} ${ry} 0 1 0 ${-2 * rx} 0 Z`;
}

/** A rounded rect as four straight runs joined by four corner arcs. */
export function roundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): string {
  const innerWidth = width - 2 * radius;
  const innerHeight = height - 2 * radius;

  return (
    `M${x + radius} ${y} h${innerWidth}` +
    ` a${radius} ${radius} 0 0 1 ${radius} ${radius}` +
    ` v${innerHeight}` +
    ` a${radius} ${radius} 0 0 1 ${-radius} ${radius}` +
    ` h${-innerWidth}` +
    ` a${radius} ${radius} 0 0 1 ${-radius} ${-radius}` +
    ` v${-innerHeight}` +
    ` a${radius} ${radius} 0 0 1 ${radius} ${-radius} Z`
  );
}

/**
 * The body frame — head, neck, torso, arms, thighs, shins, feet. Drawn
 * identically beneath both faces, and filled rather than outlined so unlit
 * muscles read as part of a body instead of shapes floating in space.
 */
export const BASE_SHAPES: readonly MuscleShape[] = [
  { d: ellipsePath(120, 40, 25, 29) },
  { d: roundedRectPath(107, 60, 26, 26, 7) },
  {
    d: 'M72,106 Q76,96 92,95 L148,95 Q164,96 168,106 L157,154 L150,206 L160,258 Q120,275 80,258 L90,206 L83,154 Z',
  },
  { d: roundedRectPath(41, 101, 26, 170, 13), rotate: [9, 54, 186] },
  { d: roundedRectPath(173, 101, 26, 170, 13), rotate: [-9, 186, 186] },
  { d: roundedRectPath(76, 258, 40, 158, 20) },
  { d: roundedRectPath(124, 258, 40, 158, 20) },
  { d: roundedRectPath(81, 404, 30, 152, 14) },
  { d: roundedRectPath(129, 404, 30, 152, 14) },
  { d: ellipsePath(96, 550, 17, 11) },
  { d: ellipsePath(144, 550, 17, 11) },
];

/** The six-pack, as three rows of two. */
const ABS_ROWS: readonly MuscleShape[] = [152, 174, 196].flatMap((y) => [
  { d: roundedRectPath(107, y, 12, 18, 5) },
  { d: roundedRectPath(121, y, 12, 18, 5) },
]);

export const FRONT_MUSCLES: readonly MuscleGroupShapes[] = [
  {
    id: 'traps',
    shapes: [
      { d: 'M118,94 L90,102 L106,116 L120,106 Z' },
      { d: 'M122,94 L150,102 L134,116 L120,106 Z' },
    ],
  },
  {
    id: 'neck',
    shapes: [
      { d: ellipsePath(112, 74, 6, 9) },
      { d: ellipsePath(128, 74, 6, 9) },
    ],
  },
  {
    id: 'shoulders',
    shapes: [
      { d: ellipsePath(83, 116, 16, 15) },
      { d: ellipsePath(157, 116, 16, 15) },
    ],
  },
  {
    id: 'chest',
    shapes: [
      { d: 'M118,114 Q98,110 89,122 Q86,138 102,145 Q117,146 118,131 Z' },
      { d: 'M122,114 Q142,110 151,122 Q154,138 138,145 Q123,146 122,131 Z' },
    ],
  },
  {
    id: 'biceps',
    shapes: [
      { d: ellipsePath(55, 158, 11, 26), rotate: [9, 55, 158] },
      { d: ellipsePath(185, 158, 11, 26), rotate: [-9, 185, 158] },
    ],
  },
  {
    id: 'forearms',
    shapes: [
      { d: ellipsePath(46, 223, 9, 30), rotate: [9, 46, 223] },
      { d: ellipsePath(194, 223, 9, 30), rotate: [-9, 194, 223] },
    ],
  },
  { id: 'abs', shapes: ABS_ROWS },
  {
    id: 'obliques',
    shapes: [
      { d: ellipsePath(95, 206, 8, 22) },
      { d: ellipsePath(145, 206, 8, 22) },
    ],
  },
  {
    id: 'hips',
    shapes: [
      { d: ellipsePath(100, 262, 11, 15) },
      { d: ellipsePath(140, 262, 11, 15) },
    ],
  },
  {
    id: 'quads',
    shapes: [
      { d: ellipsePath(96, 335, 16, 56) },
      { d: ellipsePath(144, 335, 16, 56) },
    ],
  },
  {
    id: 'adductors',
    shapes: [
      { d: ellipsePath(111, 322, 6, 42) },
      { d: ellipsePath(129, 322, 6, 42) },
    ],
  },
  {
    id: 'calves',
    shapes: [
      { d: ellipsePath(96, 462, 10, 44) },
      { d: ellipsePath(144, 462, 10, 44) },
    ],
  },
  {
    // Sits lower than the design source, which centred the ankle at y=516 —
    // inside the calf's lower half (the calf ends at 506) and a clear gap above
    // the foot (which starts at 539). At 538 the joint bridges shin and foot,
    // which is where an ankle is. Identical on both faces.
    id: 'ankles',
    shapes: [
      { d: ellipsePath(96, 538, 7, 11) },
      { d: ellipsePath(144, 538, 7, 11) },
    ],
  },
];

export const BACK_MUSCLES: readonly MuscleGroupShapes[] = [
  {
    id: 'traps',
    shapes: [{ d: 'M120,94 L92,104 L106,150 L120,160 L134,150 L148,104 Z' }],
  },
  {
    id: 'shoulders',
    shapes: [
      { d: ellipsePath(83, 116, 16, 15) },
      { d: ellipsePath(157, 116, 16, 15) },
    ],
  },
  {
    id: 'triceps',
    shapes: [
      { d: ellipsePath(55, 158, 11, 27), rotate: [9, 55, 158] },
      { d: ellipsePath(185, 158, 11, 27), rotate: [-9, 185, 158] },
    ],
  },
  {
    id: 'forearms',
    shapes: [
      { d: ellipsePath(46, 223, 9, 30), rotate: [9, 46, 223] },
      { d: ellipsePath(194, 223, 9, 30), rotate: [-9, 194, 223] },
    ],
  },
  {
    id: 'lats',
    shapes: [
      { d: 'M110,150 Q92,158 93,188 Q104,206 117,197 L115,155 Z' },
      { d: 'M130,150 Q148,158 147,188 Q136,206 123,197 L125,155 Z' },
    ],
  },
  { id: 'lower_back', shapes: [{ d: roundedRectPath(108, 196, 24, 46, 9) }] },
  {
    id: 'hips',
    shapes: [
      { d: ellipsePath(86, 260, 9, 16) },
      { d: ellipsePath(154, 260, 9, 16) },
    ],
  },
  {
    id: 'glutes',
    shapes: [
      { d: ellipsePath(98, 272, 18, 24) },
      { d: ellipsePath(142, 272, 18, 24) },
    ],
  },
  {
    id: 'hamstrings',
    shapes: [
      { d: ellipsePath(96, 348, 16, 54) },
      { d: ellipsePath(144, 348, 16, 54) },
    ],
  },
  {
    id: 'calves',
    shapes: [
      { d: ellipsePath(96, 460, 11, 46) },
      { d: ellipsePath(144, 460, 11, 46) },
    ],
  },
  {
    id: 'ankles',
    shapes: [
      { d: ellipsePath(96, 538, 7, 11) },
      { d: ellipsePath(144, 538, 7, 11) },
    ],
  },
];
